import {
  Button,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { CatalogProductType } from '../enums'
import { CatalogProduct } from '../types'
import { HeaterCatalogProduct } from '../types/CatalogProduct'
import { HeaterCatalogProductCustomDataInput } from '../types/CatalogProductInput'
import CatalogProductForm from './CatalogProductForm'
import { HeaterOutput } from '../types/Heater'
import { NOMINAL_DIAMETERS } from '../constants/nominalDiameters'
import HeatingPowerAutocomplete from './HeatingPowerAutocomplete'

export interface HeaterCatalogProductFormProps {
  catalogProduct?: HeaterCatalogProduct
  onCancel?: () => void
  onError?: (error: unknown) => void
  afterSubmit?: (catalogProduct: CatalogProduct) => void
}

export default function HeaterCatalogProductForm({
  catalogProduct,
  ...props
}: HeaterCatalogProductFormProps) {
  return (
    <CatalogProductForm
      {...props}
      type={CatalogProductType.HEATER}
      catalogProduct={catalogProduct}
    />
  )
}

export interface HeaterCatalogProductCustomDataFieldsProps {
  value: HeaterCatalogProductCustomDataInput
  catalogProduct?: CatalogProduct
  disabled?: boolean
  isError?: (field: string) => boolean
  getHelperText?: (field: string) => string | undefined
  onChange: (value: HeaterCatalogProductCustomDataInput) => void
}

export function HeaterCatalogProductCustomDataFields({
  value,
  disabled = false,
  isError,
  getHelperText,
  onChange
}: HeaterCatalogProductCustomDataFieldsProps) {
  const { t } = useTranslation('catalogProduct')

  function handleHeatingPowerChange(heatingPower: number | undefined) {
    onChange({ ...value, heatingPower })
  }

  const outputs = value.outputs ?? []

  function handleOutputsChange(outputs: HeaterOutput[]) {
    onChange({ ...value, outputs })
  }

  return (
    <FormGroup>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <HeatingPowerAutocomplete
            label={`${t('heater:heatingPower')} (kW)`}
            value={value.heatingPower}
            error={isError?.('customData.heatingPower')}
            helperText={getHelperText?.('customData.heatingPower')}
            disabled={disabled}
            fullWidth
            onChange={handleHeatingPowerChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={3}>
            <Typography variant="subtitle2">{t('pipeOutputs')}</Typography>
            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {outputs.map((output, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  secondaryAction={
                    <Tooltip title={t('actions:remove')}>
                      <span>
                        <IconButton
                          aria-label={t('actions:remove')}
                          color="error"
                          disabled={disabled}
                          onClick={() =>
                            handleOutputsChange(
                              outputs.filter((_, entryIndex) => entryIndex !== index)
                            )
                          }
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemIcon
                    sx={{ minWidth: 32, maxWidth: 32, mx: 1, justifyContent: 'center' }}
                  >
                    <Typography>{index + 1}</Typography>
                  </ListItemIcon>
                  <TextField
                    select
                    label={`${t('nominalDiameter')} (mm)`}
                    value={output.nominalDiameter}
                    disabled={disabled}
                    fullWidth
                    onChange={(event) =>
                      handleOutputsChange(
                        outputs.map((entry, entryIndex) =>
                          entryIndex === index
                            ? { nominalDiameter: Number(event.target.value) }
                            : entry
                        )
                      )
                    }
                  >
                    {NOMINAL_DIAMETERS.map((dn) => (
                      <MenuItem key={dn} value={dn}>
                        {dn}
                      </MenuItem>
                    ))}
                  </TextField>
                </ListItem>
              ))}
            </List>
            <Button
              variant="outlined"
              startIcon={<Add />}
              disabled={disabled}
              sx={{ alignSelf: 'flex-start' }}
              onClick={() =>
                handleOutputsChange([
                  ...outputs,
                  { nominalDiameter: NOMINAL_DIAMETERS[NOMINAL_DIAMETERS.length - 1] }
                ])
              }
            >
              {t('addOutput')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </FormGroup>
  )
}
